import './ManageUsers.css';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { getAllUsers, updateUser, getAllUserInstitutionsList } from '../../api/users_institutions';

function highlightText(text, query) {
    const str = String(text ?? '');
    if (!query) return str;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = str.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) => (
        i % 2 === 1 ? <span key={i} className="mu-search-highlight">{part}</span> : part
    ));
}

function getDisplayName(user) {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.username;
}

function matchesQuery(user, query) {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
        getDisplayName(user).toLowerCase().includes(q) ||
        String(user.email || '').toLowerCase().includes(q) ||
        String(user.username).toLowerCase().includes(q)
    );
}

function ModalManageAdmins({ closeModal }) {
    const { t } = useTranslation();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [newAdminInput, setNewAdminInput] = useState('');
    const [adding, setAdding] = useState(false);
    const [message, setMessage] = useState(null);
    const [confirmRemoveId, setConfirmRemoveId] = useState(null);
    const [removing, setRemoving] = useState(false);

    const fetchAdmins = useCallback(async () => {
        setLoading(true);
        try {
            const users = await getAllUsers();
            setAdmins(users.filter((u) => u.is_staff && !u.is_superuser));
        } catch (error) {
            console.error('Failed to fetch admins:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    useEffect(() => {
        const handleDocumentClick = (e) => {
            if (confirmRemoveId !== null && !e.target.closest('[data-admin-row]')) {
                setConfirmRemoveId(null);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [confirmRemoveId]);

    const handleAddAdmin = async () => {
        const query = newAdminInput.trim();
        if (!query) return;
        setMessage(null);
        setAdding(true);
        try {
            const users = await getAllUsers();
            let matchedUser = users.find((u) => String(u.username).toLowerCase() === query.toLowerCase());

            if (!matchedUser) {
                const institutions = await getAllUserInstitutionsList();
                const matchedInstitution = institutions.find((ui) => String(ui.email).toLowerCase() === query.toLowerCase());
                if (matchedInstitution) {
                    matchedUser = users.find((u) => u.id === matchedInstitution.user);
                }
            }

            if (!matchedUser) {
                setMessage({ type: 'error', text: t('manageUsers.userNotFound', { query }) });
                return;
            }
            if (matchedUser.is_staff) {
                setMessage({ type: 'error', text: t('manageUsers.alreadyAdmin') });
                return;
            }

            await updateUser(matchedUser.id, { is_staff: true });
            setNewAdminInput('');
            setMessage({ type: 'success', text: t('manageUsers.adminAdded', { name: getDisplayName(matchedUser) }) });
            await fetchAdmins();
        } catch (error) {
            console.error('Failed to add admin:', error);
            setMessage({ type: 'error', text: t('manageUsers.failedToAddAdmin') });
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveAdmin = async (userId) => {
        if (removing) return;
        setRemoving(true);
        try {
            await updateUser(userId, { is_staff: false });
            setConfirmRemoveId(null);
            await fetchAdmins();
        } catch (error) {
            console.error('Failed to remove admin:', error);
            setMessage({ type: 'error', text: t('manageUsers.failedToRemoveAdmin') });
        } finally {
            setRemoving(false);
        }
    };

    const filteredAdmins = admins.filter((user) => matchesQuery(user, search));

    return (
        <div className="mu-modal-overlay">
            <div className="mu-modal-box">
                <h2>{t('manageUsers.manageAdminsTitle')}</h2>

                <label className="mu-label-text">{t('manageUsers.addNewAdminLabel')}</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input
                        type="text"
                        className="mu-login-input"
                        style={{ marginBottom: 0 }}
                        placeholder={t('manageUsers.newAdminPlaceholder')}
                        value={newAdminInput}
                        onChange={(e) => setNewAdminInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
                        disabled={adding}
                    />
                    <button
                        className="mu-btn-primary"
                        style={{ background: '#eab308', maxHeight: '40px', marginTop: '13px' }}
                        onClick={handleAddAdmin}
                        disabled={adding || !newAdminInput.trim()}
                    >
                        {t('manageUsers.add')}
                    </button>
                </div>

                {message && (
                    <div className={`mu-alert ${message.type === 'error' ? 'mu-alert-error' : 'mu-alert-success'}`}>
                        {message.text}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px', marginTop: '22px' }}>
                    <label className="mu-label-text" style={{ marginBottom: 0 }}>{t('manageUsers.currentAdmins')}</label>
                    <input
                        type="text"
                        className="mu-login-input"
                        style={{ width: '200px', padding: '6px 12px', marginBottom: 0, fontSize: '0.85rem' }}
                        placeholder={t('manageUsers.searchAdminsPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <ul className="mu-user-list-container">
                    {!loading && filteredAdmins.length === 0 && (
                        <li className="mu-empty-list-text">{t('manageUsers.noAdminsFound')}</li>
                    )}
                    {filteredAdmins.map((user) => (
                        <li key={user.id} className="mu-user-list-item" data-admin-row>
                            <div className="mu-user-info">
                                <span className="mu-user-name">{highlightText(getDisplayName(user), search)}</span>
                                <span className="mu-user-email">{highlightText(user.email || user.username, search)}</span>
                            </div>
                            {confirmRemoveId === user.id ? (
                                <button className="mu-confirm-btn" onClick={() => handleRemoveAdmin(user.id)} disabled={removing}>
                                    {t('manageUsers.confirmRemove')}
                                </button>
                            ) : (
                                <button
                                    className="mu-remove-btn"
                                    onClick={(e) => { e.stopPropagation(); setConfirmRemoveId(user.id); }}
                                >
                                    ✕
                                </button>
                            )}
                        </li>
                    ))}
                </ul>

                <div className="mu-modal-actions">
                    <button className="mu-btn-cancel" onClick={closeModal}>{t('manageUsers.close')}</button>
                </div>
            </div>
        </div>
    );
}

function ModalViewSuperadmins({ closeModal }) {
    const { t } = useTranslation();
    const [superadmins, setSuperadmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const fetchSuperadmins = async () => {
            setLoading(true);
            try {
                const users = await getAllUsers();
                setSuperadmins(users.filter((u) => u.is_superuser));
            } catch (error) {
                console.error('Failed to fetch superadmins:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSuperadmins();
    }, []);

    const filteredSuperadmins = superadmins.filter((user) => matchesQuery(user, search));

    return (
        <div className="mu-modal-overlay">
            <div className="mu-modal-box">
                <h2>{t('manageUsers.viewSuperadminsTitle')}</h2>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                    <label className="mu-label-text" style={{ marginBottom: 0 }}>{t('manageUsers.currentSuperadmins')}</label>
                    <input
                        type="text"
                        className="mu-login-input"
                        style={{ width: '200px', padding: '6px 12px', marginBottom: 0, fontSize: '0.85rem' }}
                        placeholder={t('manageUsers.searchSuperadminsPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <ul className="mu-user-list-container">
                    {!loading && filteredSuperadmins.length === 0 && (
                        <li className="mu-empty-list-text">{t('manageUsers.noSuperadminsFound')}</li>
                    )}
                    {filteredSuperadmins.map((user) => (
                        <li key={user.id} className="mu-user-list-item">
                            <div className="mu-user-info">
                                <span className="mu-user-name">{highlightText(getDisplayName(user), search)}</span>
                                <span className="mu-user-email">{highlightText(user.email || user.username, search)}</span>
                            </div>
                        </li>
                    ))}
                </ul>

                <div className="mu-modal-actions">
                    <button className="mu-btn-cancel" onClick={closeModal}>{t('manageUsers.close')}</button>
                </div>
            </div>
        </div>
    );
}

function ManageUsers({ view, closeModal }) {
    if (view === 'superadmins') {
        return <ModalViewSuperadmins closeModal={closeModal} />;
    }
    return <ModalManageAdmins closeModal={closeModal} />;
}

export default ManageUsers;
